'use strict';

/**
 * HARDEN-6 — Master-key envelope encryption
 *
 * W2 of PLAN-SEAMS-MULTIUSER-V1.md — corrected implementation
 *
 * T102 W1 fix: the original implementation used positional args but all
 * callers (envelope-dispatch.js, all tests) use object-style named args.
 * This rewrite matches the spec pinned in __tests__/crypto/master-key-encryption.test.js.
 *
 * Envelope format (v:1):
 * {
 *   v: 1,
 *   algo: 'aes-256-gcm',     // cipher for data encryption
 *   kdf: 'hkdf-sha512-v1',   // identifies DEK derivation (not the master-key KDF)
 *   recordType: '<string>',   // domain-separation label for HKDF
 *   recordId: '<string>',     // per-record identity for HKDF
 *   iv: '<hex>',              // AES-GCM IV for data encryption (12 bytes, FRESH per write)
 *   ct: '<hex>',              // AES-GCM(dek, plaintext): encrypted data
 *   tag: '<hex>',             // AES-GCM auth tag (16 bytes)
 *   wrappedDek: null,         // v1: reserved (null). v2: AES-GCM(masterKey, dek)
 * }
 *
 * DEK derivation:
 *   dek = HKDF-SHA512(masterKey, salt='', info='oyster:master-key-data:v1:<recordType>:<recordId>', 32)
 *
 * ── T901 P1 — envelope v:2 (RANDOM, STORED, WRAPPED DEK) ────────────────────
 * v1's DEK is DERIVED from the master key, so rotating the master key
 * invalidates every v1 record unless its ciphertext is re-encrypted — and a
 * re-encrypt sweep that misses one store is indistinguishable from a
 * crypto-shred of that store (GOTCHA-346, plan LD-2). v2 makes the DEK random
 * and stored, so rotation re-wraps 32 bytes per record and NEVER touches
 * ciphertext (LD-1):
 * {
 *   v: 2,
 *   algo: 'aes-256-gcm',
 *   kdf: 'wrapped-dek-v2',    // identifies DEK provenance: random, wrapped
 *   recordType, recordId,     // REQUIRED non-empty (canonical object API only)
 *   iv, ct, tag,              // AES-GCM(dek, plaintext) — dek = randomBytes(32)
 *   wrappedDek: { iv, ct, tag } // AES-GCM(kek, dek)
 * }
 * kek = HKDF-SHA512(masterKey, salt='', info='oyster:master-key-dek-wrap:v2:<rt>:<rid>', 32)
 * The v2 label is ADDITIVE (LD-6 — the v1 label is locked forever and
 * untouched). Wire format pinned by __tests__/crypto/t901-p1-v2-envelope.test.js
 * P1/e (raw-crypto unwrap) — changing it strands every v2 record.
 *
 * v2 writes are OPT-IN (dekWrap:true). The default writer stays v1 until the
 * LD-3 ordering is satisfied everywhere a reader lives (dual-read shipped
 * FIRST; the flip is a later, explicit decision — never a side effect here).
 *
 * Security properties:
 *   - DEK is deterministic per (masterKey, recordType, recordId)
 *   - IV is fresh-random per write — GCM IV reuse would be catastrophic
 *   - Wrong master key → GCM auth tag fails → returns null (non-throwing)
 *   - Each record uses a different DEK (domain separation via recordType:recordId)
 *
 * THE BRIDGE PATTERN IS FORBIDDEN HERE.
 * encryptCredential(*, sessionSecret) MUST NEVER appear in this file.
 * The W0 governance test INV-1 enforces this via regex scan.
 */

const crypto = require('crypto');
const { x25519 } = require('@noble/curves/ed25519.js');
const { ml_kem768 } = require('@noble/post-quantum/ml-kem.js');
const hybridEnvelope = require('./hybrid-envelope.js');
const { MAIL_HYBRID_V1 } = require('./algo-versions.js');

const ALGO = 'aes-256-gcm';
const KEY_LEN = 32;   // 256-bit
const IV_LEN = 12;    // 96-bit, NIST recommended for AES-GCM
const TAG_LEN = 16;   // 128-bit authentication tag
const DEK_LEN = 32;   // 256-bit data-encryption key

// T323 P6 (LD-22) — deterministic hybrid recipient keypair derived from the user
// master key, for the optional hybrid-PQ wrap of HNDL-sensitive blobs (mail
// private-key escrow). Distinct HKDF labels keep these seeds independent of the
// data-DEK derivation. The X25519 seed is 32 bytes; ML-KEM-768 keygen needs 64.
const HYBRID_X25519_LABEL = 'oyster:master-key-hybrid:x25519:v1';
const HYBRID_MLKEM_LABEL = 'oyster:master-key-hybrid:mlkem768:v1';

function _deriveHybridKeypair(userMasterKey, recordType, recordId) {
  const ctx = ':' + recordType + ':' + recordId;
  const xSeed = Buffer.from(crypto.hkdfSync('sha512', userMasterKey, Buffer.alloc(0), Buffer.from(HYBRID_X25519_LABEL + ctx), 32));
  const kemSeed = Buffer.from(crypto.hkdfSync('sha512', userMasterKey, Buffer.alloc(0), Buffer.from(HYBRID_MLKEM_LABEL + ctx), 64));
  const xPriv = new Uint8Array(xSeed);
  const xPub = x25519.getPublicKey(xPriv);
  const kemKp = ml_kem768.keygen(new Uint8Array(kemSeed));
  xSeed.fill(0); kemSeed.fill(0);
  return {
    publicKey: { x25519: xPub, mlKem: kemKp.publicKey },
    privateKey: { x25519: xPriv, mlKem: kemKp.secretKey },
  };
}

// HKDF info label — locked forever per test HARDEN-6-W-B (deriveDek label pin).
// Changing this would silently invalidate every shipped envelope.
const HKDF_INFO_PREFIX = 'oyster:master-key-data:v1:';

// T901 P1 — v2 DEK-wrap KEK label. ADDITIVE beside the locked v1 label (LD-6);
// pinned by t901-p1-v2-envelope.test.js P1/e. Changing it strands every v2
// record. Mirrored as a literal in the browser reader
// (shared/cores/dings/client/encrypted-source.js) — the P2 suite asserts the
// two stay byte-identical.
const DEK_WRAP_INFO_PREFIX_V2 = 'oyster:master-key-dek-wrap:v2:';
const KDF_V2 = 'wrapped-dek-v2';

function _assertKey(key, name) {
  if (!Buffer.isBuffer(key) && !(key instanceof Uint8Array)) {
    throw new TypeError(name + ' must be a Buffer or Uint8Array');
  }
  if (key.length !== KEY_LEN) {
    throw new Error(name + ' must be exactly 32 bytes (got ' + key.length + ')');
  }
  // S122 FIX-C — refuse the all-zero key. A legitimate master key is never
  // all zeroes; one arriving here means a zeroized (evicted) cache buffer
  // reached a crypto operation (use-after-zeroize). Without this, HKDF would
  // silently derive a DEK from the publicly-known all-zero key and seal 🔴
  // content under it. OR-accumulate = no data-dependent early exit. Decrypt
  // paths catch this throw and stay non-throwing (return null, fail closed).
  let acc = 0;
  for (let i = 0; i < key.length; i++) acc |= key[i];
  if (acc === 0) {
    throw new Error(name + ' must not be all-zero (zeroized key — use-after-evict refused)');
  }
}

/**
 * Derive a deterministic DEK from the master key + record identity via HKDF-SHA512.
 *
 * @param {object} opts
 * @param {Buffer|Uint8Array} opts.userMasterKey
 * @param {string} opts.recordType
 * @param {string} opts.recordId
 * @returns {Buffer} 32-byte DEK
 */
function _assertRecordRef(value, name) {
  if (typeof value !== 'string' || value.length === 0) {
    throw new TypeError(name + ' must be a non-empty string');
  }
  if (value.includes(':')) {
    throw new TypeError(
      name + ' must not contain ":" — colon is the HKDF info-label field separator '
      + 'and its presence creates DEK-collision attack surface (colon-injection)',
    );
  }
}

function deriveDek({ userMasterKey, recordType, recordId } = {}) {
  _assertKey(userMasterKey, 'userMasterKey');
  _assertRecordRef(recordType, 'recordType');
  _assertRecordRef(recordId, 'recordId');
  const info = Buffer.from(
    HKDF_INFO_PREFIX + recordType + ':' + recordId,
    'utf8',
  );
  return Buffer.from(crypto.hkdfSync('sha512', userMasterKey, Buffer.alloc(0), info, DEK_LEN));
}

// ── T901 P1 — v2 DEK wrap/unwrap primitives ──────────────────────────────────

/**
 * Derive the per-record KEK that wraps a v2 DEK. Distinct HKDF label keeps it
 * independent of the v1 data-DEK derivation (and of the hybrid seeds).
 * Same colon-injection rules as deriveDek: recordType/recordId are REQUIRED
 * non-empty and colon-free.
 */
function _deriveDekWrapKek({ userMasterKey, recordType, recordId }) {
  _assertKey(userMasterKey, 'userMasterKey');
  _assertRecordRef(recordType, 'recordType');
  _assertRecordRef(recordId, 'recordId');
  const info = Buffer.from(
    DEK_WRAP_INFO_PREFIX_V2 + recordType + ':' + recordId,
    'utf8',
  );
  return Buffer.from(crypto.hkdfSync('sha512', userMasterKey, Buffer.alloc(0), info, KEY_LEN));
}

/** AES-256-GCM wrap of a 32-byte DEK under a KEK. Fresh IV per wrap. */
function _wrapDek(dek, kek) {
  const iv = crypto.randomBytes(IV_LEN);
  const cipher = crypto.createCipheriv(ALGO, kek, iv);
  const ct = Buffer.concat([cipher.update(dek), cipher.final()]);
  return {
    iv: iv.toString('hex'),
    ct: ct.toString('hex'),
    tag: cipher.getAuthTag().toString('hex'),
  };
}

/**
 * Unwrap a v2 wrappedDek under a KEK. NON-THROWING: returns the 32-byte DEK
 * Buffer, or null on wrong key / tamper / malformed shape (fail closed).
 */
function _unwrapDek(wrappedDek, kek) {
  try {
    if (wrappedDek === null || typeof wrappedDek !== 'object') return null;
    const iv = Buffer.from(wrappedDek.iv, 'hex');
    const tag = Buffer.from(wrappedDek.tag, 'hex');
    const ct = Buffer.from(wrappedDek.ct, 'hex');
    if (iv.length !== IV_LEN || tag.length !== TAG_LEN) return null;
    const decipher = crypto.createDecipheriv(ALGO, kek, iv);
    decipher.setAuthTag(tag);
    const dek = Buffer.concat([decipher.update(ct), decipher.final()]);
    if (dek.length !== DEK_LEN) { dek.fill(0); return null; }
    return dek;
  } catch (_e) {
    return null;
  }
}

/**
 * Encrypt plaintext under the user's master key.
 *
 * @param {object} opts
 * @param {string|Buffer|Uint8Array} opts.plaintext
 * @param {Buffer|Uint8Array} opts.userMasterKey — 32-byte master key
 * @param {string} opts.recordType — domain-separation label for HKDF
 * @param {string} opts.recordId — per-record identity for HKDF
 * @returns {object} envelope
 */
function encryptUnderMasterKey(arg1, arg2) {
  // Dual signature (compat): callers may pass either
  //   encryptUnderMasterKey({ plaintext, userMasterKey, recordType, recordId })  ← canonical (object-API)
  //   encryptUnderMasterKey(plaintext, userMasterKey)                            ← legacy positional
  // The positional form derives an undifferentiated DEK (recordType/recordId
  // default to ''), which is exactly what the legacy consumers relied on.
  let plaintext;
  let userMasterKey;
  let recordType;
  let recordId;
  let hybridWrap = false;
  let dekWrap = false;
  if (
    arg1 !== null
    && typeof arg1 === 'object'
    && !Buffer.isBuffer(arg1)
    && !(arg1 instanceof Uint8Array)
    && arg2 === undefined
  ) {
    ({ plaintext, userMasterKey, recordType, recordId, hybridWrap, dekWrap } = arg1);
  } else {
    plaintext = arg1;
    userMasterKey = arg2;
    // Legacy positional callers carry no record identity. Use fixed,
    // non-empty domain labels so the DEK derivation is deterministic and the
    // labels are persisted into the envelope (decrypt re-derives from them).
    // NOTE: this default applies to the POSITIONAL branch only — the object-API
    // continues to REQUIRE non-empty recordType/recordId (deriveDek enforces it).
    recordType = 'legacy';
    recordId = 'positional';
  }
  _assertKey(userMasterKey, 'userMasterKey');

  // ── T323 P6 (LD-22): optional hybrid-PQ wrap for HNDL-sensitive blobs ──
  // The mail private-key escrow blob (mail-keypair.wrapMailPrivateKey) opts in
  // via hybridWrap:true. The plaintext is sealed under a hybrid (X25519+ML-KEM-768)
  // envelope keyed to a keypair DETERMINISTICALLY derived from the user master key
  // (so recovery re-derives it). The envelope routes through hybrid-envelope.js
  // (LD-18) and is algo-tagged MAIL_HYBRID_V1 (carries the ML-KEM component).
  // Existing AES-256-GCM-only blobs are UNAFFECTED — they have no hybridWrap flag
  // and decrypt via the classical path (back-compat / lazy re-wrap on next write).
  if (hybridWrap) {
    // ── S271 W1 — NO defaulting here. ────────────────────────────────────
    // This branch previously read:
    //   const rt  = (typeof recordType === 'string' && recordType) ? recordType : 'legacy';
    //   const rid = (typeof recordId   === 'string' && recordId)   ? recordId   : 'positional';
    // The `_assertRecordRef` calls below sat DOWNSTREAM of a ternary whose
    // terminal arm supplied a non-empty literal, so the empty/non-string arm
    // could never fire — the GOTCHA-1050 shape (a chain whose terminal grants
    // the permissive outcome, with a guard placed after it that READS as
    // enforcement). Passing `hybridWrap:true` therefore DOWNGRADED an enforced
    // requirement to a silent default, re-minting the forbidden
    // `legacy:positional` / `<recordType>:positional` pinned-DEK shape without
    // the caller asking and without an error (CLAUDE.md §Legal Foundation).
    //
    // The caller's values now go straight to the assert, identically to
    // deriveDek and _deriveDekWrapKek — which is what makes the comment above
    // the positional branch ("the object-API continues to REQUIRE non-empty
    // recordType/recordId") true on ALL THREE write paths.
    //
    // ⚠ Both arms of _assertRecordRef are load-bearing and BOTH are retained.
    // The colon arm was always LIVE on this branch (a value containing ':'
    // survives a ternary), and it must stay: info = PREFIX + recordType + ':'
    // + recordId, so without it (type:'a', id:'b:c') and (type:'a:b', id:'c')
    // derive the SAME keypair.
    //
    // ⛔ The READ side (decryptUnderMasterKey's `envelope.recordType || 'legacy'`)
    // is NOT touched: those defaults are compat for envelopes already on disk.
    // Tightening write and read in one edit is how this fix becomes data loss.
    _assertRecordRef(recordType, 'recordType');
    _assertRecordRef(recordId, 'recordId');
    const rt = recordType;
    const rid = recordId;
    const kp = _deriveHybridKeypair(userMasterKey, rt, rid);
    const inner = hybridEnvelope.wrapEnvelope(plaintext, kp.publicKey);
    kp.privateKey.x25519.fill(0); kp.privateKey.mlKem.fill(0);
    return {
      v: 1,
      algo: MAIL_HYBRID_V1, // the X25519+ML-KEM-768 hybrid mail-blob tag
      kdf: 'hkdf-sha512-v1',
      recordType: rt,
      recordId: rid,
      hybrid: inner, // { algo, kemCiphertext, x25519EphemeralPublic, iv, ct, tag }
    };
  }

  // ── T901 P1 — v2 opt-in write path: RANDOM DEK, wrapped under the master
  // key. Object-API only (the positional form cannot express dekWrap, and v2
  // REQUIRES a real record identity — _deriveDekWrapKek enforces non-empty,
  // colon-free recordType/recordId, so the legacy 'legacy:positional'
  // shared-DEK shape is unmintable as v2 by construction). ──
  if (dekWrap) {
    const dek = crypto.randomBytes(DEK_LEN); // RANDOM — never derived (LD-1)
    const kek = _deriveDekWrapKek({ userMasterKey, recordType, recordId });
    const iv = crypto.randomBytes(IV_LEN); // MUST be fresh per write
    const plainBuf = typeof plaintext === 'string'
      ? Buffer.from(plaintext, 'utf8')
      : Buffer.from(plaintext);
    const cipher = crypto.createCipheriv(ALGO, dek, iv);
    const ct = Buffer.concat([cipher.update(plainBuf), cipher.final()]);
    const tag = cipher.getAuthTag();
    const wrappedDek = _wrapDek(dek, kek);
    dek.fill(0);
    kek.fill(0);
    return {
      v: 2,
      algo: ALGO,
      kdf: KDF_V2,
      recordType,
      recordId,
      iv: iv.toString('hex'),
      ct: ct.toString('hex'),
      tag: tag.toString('hex'),
      wrappedDek,
    };
  }

  // recordType/recordId validation (including colon-rejection) is enforced by deriveDek
  const dek = deriveDek({ userMasterKey, recordType, recordId });
  const iv = crypto.randomBytes(IV_LEN); // MUST be fresh per write
  const plainBuf = typeof plaintext === 'string'
    ? Buffer.from(plaintext, 'utf8')
    : Buffer.from(plaintext);
  const cipher = crypto.createCipheriv(ALGO, dek, iv);
  const ct = Buffer.concat([cipher.update(plainBuf), cipher.final()]);
  const tag = cipher.getAuthTag();
  dek.fill(0);
  return {
    v: 1,
    algo: ALGO,
    kdf: 'hkdf-sha512-v1',
    recordType: recordType || '',
    recordId: recordId || '',
    iv: iv.toString('hex'),
    ct: ct.toString('hex'),
    tag: tag.toString('hex'),
    wrappedDek: null,
  };
}

/**
 * Decrypt an envelope produced by encryptUnderMasterKey().
 *
 * Non-throwing. Returns null on: null/undefined envelope, wrong key,
 * tampered ciphertext, malformed envelope.
 *
 * @param {object} opts
 * @param {object} opts.envelope
 * @param {Buffer|Uint8Array} opts.userMasterKey — 32-byte master key
 * @returns {string|null}
 */
function decryptUnderMasterKey(arg1, arg2) {
  // Dual signature (compat): callers may pass either
  //   decryptUnderMasterKey({ envelope, userMasterKey })   ← canonical (object-API)
  //   decryptUnderMasterKey(envelope, masterKey)           ← legacy positional
  // An object that is itself a master-key envelope (has v:1/algo/ct) is the
  // POSITIONAL first arg, not the wrapper — disambiguate via isMasterKeyEnvelope.
  let envelope;
  let userMasterKey;
  if (
    arg2 === undefined
    && arg1 !== null
    && typeof arg1 === 'object'
    && !isMasterKeyEnvelope(arg1)
    && ('envelope' in arg1 || 'userMasterKey' in arg1)
  ) {
    ({ envelope, userMasterKey } = arg1);
  } else {
    envelope = arg1;
    userMasterKey = arg2;
  }
  if (envelope == null) return null;
  if (!envelope || typeof envelope !== 'object') return null;

  // ── T323 P6 (LD-22): hybrid-PQ blob path. Detect the hybrid algo tag and
  // unwrap via the deterministic master-key-derived hybrid keypair. Returns null
  // (non-throwing) on the wrong key — custodian-of-ciphertext. ──
  if (envelope.algo === MAIL_HYBRID_V1 && envelope.hybrid) {
    try {
      _assertKey(userMasterKey, 'userMasterKey');
      const kp = _deriveHybridKeypair(userMasterKey, envelope.recordType || 'legacy', envelope.recordId || 'positional');
      let plain;
      try {
        plain = hybridEnvelope.unwrapEnvelope(envelope.hybrid, kp.privateKey);
      } finally {
        kp.privateKey.x25519.fill(0); kp.privateKey.mlKem.fill(0);
      }
      return Buffer.from(plain).toString('utf8');
    } catch (_e) {
      return null; // wrong key / tampered / downgrade — fail closed, non-throwing
    }
  }

  // ── T901 P1 — v2 read path: unwrap the stored DEK, then open ct with it.
  // Version discrimination on read; NON-THROWING null on every failure
  // (wrong key, tampered wrappedDek, tampered ct, malformed shape). ──
  if (envelope.v === 2) {
    try {
      _assertKey(userMasterKey, 'userMasterKey');
      const kek = _deriveDekWrapKek({
        userMasterKey,
        recordType: envelope.recordType,
        recordId: envelope.recordId,
      });
      const dek = _unwrapDek(envelope.wrappedDek, kek);
      kek.fill(0);
      if (dek == null) return null; // wrong key / tampered wrap — fail closed
      const iv = Buffer.from(envelope.iv, 'hex');
      const tag = Buffer.from(envelope.tag, 'hex');
      const ct = Buffer.from(envelope.ct, 'hex');
      const decipher = crypto.createDecipheriv(ALGO, dek, iv);
      decipher.setAuthTag(tag);
      let plainBuf;
      try {
        plainBuf = Buffer.concat([decipher.update(ct), decipher.final()]);
      } catch (_e) {
        dek.fill(0);
        return null; // auth tag failed — tampered ciphertext
      }
      dek.fill(0);
      return plainBuf.toString('utf8');
    } catch (_e) {
      return null;
    }
  }

  try {
    _assertKey(userMasterKey, 'userMasterKey');
    const dek = deriveDek({
      userMasterKey,
      recordType: envelope.recordType || '',
      recordId: envelope.recordId || '',
    });
    const iv = Buffer.from(envelope.iv, 'hex');
    const tag = Buffer.from(envelope.tag, 'hex');
    const ct = Buffer.from(envelope.ct, 'hex');
    const decipher = crypto.createDecipheriv(ALGO, dek, iv);
    decipher.setAuthTag(tag);
    let plainBuf;
    try {
      plainBuf = Buffer.concat([decipher.update(ct), decipher.final()]);
    } catch (_e) {
      dek.fill(0);
      return null; // auth tag failed — wrong key or tampered
    }
    dek.fill(0);
    return plainBuf.toString('utf8');
  } catch (_e) {
    return null;
  }
}

/**
 * Return true if value looks like a master-key envelope.
 *
 * T347 P4: a master-key envelope is EITHER the classical AES-256-GCM shape
 * (v:1, algo:'aes-256-gcm') OR the hybrid-PQ shape produced by
 * encryptUnderMasterKey({ hybridWrap:true }) (algo:MAIL_HYBRID_V1 + a `hybrid`
 * sub-object). Both decrypt under decryptUnderMasterKey with the user master key;
 * recognizing the hybrid shape here means every dual-read call site that routes
 * on isMasterKeyEnvelope (28 stores) correctly sends a hybrid envelope to the
 * master-key path instead of mis-routing it into a legacy decoder. This is the
 * single-point fix for the hybrid dual-read; the value is still NOT a legacy
 * SESSION_SECRET envelope (algo 'v1.aes256gcm') — those stay false.
 *
 * @param {*} value
 * @returns {boolean}
 */
const HEX_RE = /^[0-9a-f]+$/i;

function isMasterKeyEnvelope(value) {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  // Hybrid-PQ master-key envelope (encryptUnderMasterKey hybridWrap:true).
  if (value.algo === MAIL_HYBRID_V1 && value.hybrid && typeof value.hybrid === 'object') return true;
  // T901 P2 — v:2 (wrapped-DEK) envelope. Accepting it HERE is the dual-read:
  // isMasterKeyEnvelope is the single-point router for every dual-read call
  // site AND for rewrapAllStores' sweep — before this branch, a v2 envelope
  // was invisible to both (unreadable AND unrotatable: the orphaning defect
  // LD-3's ordering exists to prevent). Fail-closed on shape: v:2 WITHOUT a
  // well-formed wrappedDek is refused, never "close enough".
  if (value.v === 2) {
    if (value.algo !== 'aes-256-gcm') return false;
    if (typeof value.kdf !== 'string' || value.kdf.length === 0) return false;
    if (typeof value.iv !== 'string' || value.iv.length !== 24 || !HEX_RE.test(value.iv)) return false;
    if (typeof value.tag !== 'string' || value.tag.length !== 32 || !HEX_RE.test(value.tag)) return false;
    if (typeof value.ct !== 'string' || value.ct.length % 2 !== 0 || (value.ct.length > 0 && !HEX_RE.test(value.ct))) return false;
    const w = value.wrappedDek;
    if (w === null || typeof w !== 'object' || Array.isArray(w)) return false;
    if (typeof w.iv !== 'string' || w.iv.length !== 24 || !HEX_RE.test(w.iv)) return false;
    if (typeof w.tag !== 'string' || w.tag.length !== 32 || !HEX_RE.test(w.tag)) return false;
    // wrapped DEK ct: exactly 32 bytes (GCM ct length == plaintext length)
    if (typeof w.ct !== 'string' || w.ct.length !== 64 || !HEX_RE.test(w.ct)) return false;
    return true;
  }
  if (value.v !== 1) return false;
  if (value.algo !== 'aes-256-gcm') return false;
  if (typeof value.kdf !== 'string' || value.kdf.length === 0) return false;
  // iv: exactly 24 hex chars (12-byte GCM IV)
  if (typeof value.iv !== 'string' || value.iv.length !== 24 || !HEX_RE.test(value.iv)) return false;
  // tag: exactly 32 hex chars (16-byte GCM auth tag)
  if (typeof value.tag !== 'string' || value.tag.length !== 32 || !HEX_RE.test(value.tag)) return false;
  // ct: non-null hex string with even length
  if (typeof value.ct !== 'string' || value.ct.length % 2 !== 0 || (value.ct.length > 0 && !HEX_RE.test(value.ct))) return false;
  return true;
}

/**
 * T901 P1 — rotation's unit primitive: re-wrap an envelope's DEK from
 * oldMasterKey → newMasterKey WITHOUT touching the ciphertext (LD-1).
 *
 *   - v2 in → v2 out: unwrap the stored DEK under the old key's KEK, wrap it
 *     under the new key's KEK. iv/ct/tag are copied byte-identical.
 *   - v1 in → v2 out (upgrade, "populate wrappedDek"): the v1 DEK is
 *     re-DERIVED from the old key and wrapped under the new key's KEK.
 *     ciphertext byte-identical. ⚠ DECRYPT-GATED first: deriving is possible
 *     under ANY 32-byte key, so an ungated upgrade under a wrong old key
 *     would wrap a garbage DEK and permanently strand the record — the shred
 *     wearing the safe op's name (LD-2).
 *     Residual, named: a v1 DEK stays re-derivable by a holder of the OLD
 *     master key. Password-change rotation EVICTS the old key; it does not
 *     treat it as compromised. Compromise-grade rotation of v1 records needs
 *     re-encryption — P3 policy, not this verb.
 *
 * FAIL-CLOSED THROWS, never fallbacks (rewrapMailPrivateKey doctrine — plan
 * LD-2/LD-14: "a rewrap that silently regenerates is indistinguishable from a
 * crypto-shred"):
 *   - ERR_REWRAP_OLD_KEY_CANNOT_UNWRAP — the old key does not open this
 *     envelope. NEVER regenerates a DEK.
 *   - ERR_REWRAP_UNSUPPORTED_ENVELOPE — hybrid blobs (no wrappedDek; the
 *     sweep's decrypt/re-encrypt path owns them) and non-envelopes.
 *
 * @param {object} opts
 * @param {object} opts.envelope — a v1 or v2 master-key envelope
 * @param {Buffer|Uint8Array} opts.oldMasterKey
 * @param {Buffer|Uint8Array} opts.newMasterKey
 * @returns {object} a v2 envelope with identical iv/ct/tag and a fresh wrappedDek
 */
function rewrapWrappedDek({ envelope, oldMasterKey, newMasterKey } = {}) {
  _assertKey(oldMasterKey, 'oldMasterKey');
  _assertKey(newMasterKey, 'newMasterKey');
  if (
    !isMasterKeyEnvelope(envelope)
    || (envelope.algo === MAIL_HYBRID_V1)
    || envelope.hybrid
  ) {
    const err = new Error('rewrapWrappedDek: not a re-wrappable master-key envelope (v1/v2 aes-256-gcm only)');
    err.code = 'ERR_REWRAP_UNSUPPORTED_ENVELOPE';
    throw err;
  }
  const recordType = envelope.recordType;
  const recordId = envelope.recordId;

  let dek;
  if (envelope.v === 2) {
    const oldKek = _deriveDekWrapKek({ userMasterKey: oldMasterKey, recordType, recordId });
    dek = _unwrapDek(envelope.wrappedDek, oldKek);
    oldKek.fill(0);
    if (dek == null) {
      const err = new Error('rewrapWrappedDek: the old master key cannot unwrap this envelope\'s DEK — refusing (a re-wrap that regenerates is a crypto-shred)');
      err.code = 'ERR_REWRAP_OLD_KEY_CANNOT_UNWRAP';
      throw err;
    }
  } else {
    // v1 upgrade — decrypt gate BEFORE deriving (see JSDoc).
    const probe = decryptUnderMasterKey({ envelope, userMasterKey: oldMasterKey });
    if (probe == null) {
      const err = new Error('rewrapWrappedDek: the old master key does not open this v1 envelope — refusing to upgrade (an ungated derive wraps a garbage DEK: a shred)');
      err.code = 'ERR_REWRAP_OLD_KEY_CANNOT_UNWRAP';
      throw err;
    }
    dek = deriveDek({ userMasterKey: oldMasterKey, recordType, recordId });
  }

  const newKek = _deriveDekWrapKek({ userMasterKey: newMasterKey, recordType, recordId });
  const wrappedDek = _wrapDek(dek, newKek);
  dek.fill(0);
  newKek.fill(0);
  return {
    v: 2,
    algo: ALGO,
    kdf: KDF_V2,
    recordType,
    recordId,
    iv: envelope.iv,
    ct: envelope.ct,
    tag: envelope.tag,
    wrappedDek,
  };
}

module.exports = {
  encryptUnderMasterKey,
  decryptUnderMasterKey,
  isMasterKeyEnvelope,
  deriveDek,
  // T901 P1 — v2 wrapped-DEK surface
  rewrapWrappedDek,
  DEK_WRAP_INFO_PREFIX_V2,
};
